import { Box, IconButton } from '@mui/material';
import { colors } from 'styles/colors';
import CaseDocIcon from 'icons/CaseDocIcon';
import ImageCarousel from 'components/modals/ImageCarousel/ImageCarousel';
import CustomText from 'components/common/CustomText/CustomText';
import { useState } from 'react';
import { ImageItem } from 'types/types/Types';
import { hexWithOpacity } from 'common/utils';

interface Props {
  item: any;
  onNavigate?: () => void;
}

export default function CalendarCaseItem({ item, onNavigate }: Props) {
  const [carouselOpen, setCarouselOpen] = useState(false);

  const ageLabel = item.age && item.age >= 1 ? `${item.age}y` : null;

  const genderLabel = item?.gender
    ? item.gender.charAt(0).toUpperCase() + item.gender.slice(1).toLowerCase()
    : '';

  const procedureLabel =
    item.procedure_name && item.procedure_name !== 'undefined'
      ? item.procedure_name
      : item.type;

  const hasMedia = item?.media?.length > 0;

  return (
    <>
      <Box
        sx={{
          backgroundColor: colors.white,
          border: `1px solid ${colors.border}`,
          borderRadius: '10px',
          p: 1.5,
          mb: 0,
          mx: 0.5,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.5,
          transition: 'background-color 0.1s ease',
          '&:hover': {
            backgroundColor: hexWithOpacity(colors.topBackground, 0.6),
          },
        }}
      >
        {/* Thumbnail — matches AllPatientsTable */}
        <Box sx={{ flexShrink: 0 }}>
          {hasMedia ? (
            <IconButton
              sx={{ padding: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                setCarouselOpen(true);
              }}
            >
              <Box
                component="img"
                src={item.media[0]?.urls?.href_small}
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: '6px',
                  objectFit: 'cover',
                }}
              />
            </IconButton>
          ) : (
            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: '6px',
                backgroundColor: colors.topBackground,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CaseDocIcon color={colors.lightgray} size={40} />
            </Box>
          )}
        </Box>

        {/* Text info — clicking here navigates to the patient profile */}
        <Box
          onClick={onNavigate}
          sx={{
            overflow: 'hidden',
            flex: 1,
            cursor: onNavigate ? 'pointer' : 'default',
          }}
        >
          {/* Patient name + age/gender */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <CustomText
              noWrap
              text={`${item.patient_last_name}, ${item.patient_first_name}`}
              fontSize={15}
              fontWeight={600}
              sx={{ color: colors.primary }}
            />
            {ageLabel && (
              <CustomText
                text={`${ageLabel} · ${genderLabel}`}
                fontSize={13}
                sx={{ color: colors.secondary, flexShrink: 0 }}
              />
            )}
          </Box>

          {/* Institution */}
          {item?.hospital?.name && (
            <CustomText
              noWrap
              text={item.hospital.name}
              fontSize={14}
              fontWeight={500}
              sx={{ color: colors.text }}
            />
          )}

          {/* Procedure */}
          {procedureLabel && (
            <CustomText
              noWrap
              text={procedureLabel}
              fontSize={13}
              fontWeight={600}
              sx={{ color: colors.secondary }}
            />
          )}
        </Box>
      </Box>

      <ImageCarousel
        open={carouselOpen}
        images={item.media as ImageItem[]}
        onClose={() => setCarouselOpen(false)}
      />
    </>
  );
}
